import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { usePermissions } from "@/components/auth";
import { 
  Users, 
  FileText, 
  Target, 
  DollarSign, 
  Percent, 
  Handshake, 
  Zap,
  BatteryCharging,
  TrendingUp,
  TrendingDown,
  Calendar
} from "lucide-react";
import { getResumoComissaoCarregadores } from '@/services/carregadoresComissaoService';
import { supabase } from '@/integrations/supabase/client';
import { PRODUTOS_LEAD_CARREGADOR, supabaseOrProdutoNaoCarregador } from '@/constants/leadsProduto';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  Cell,
} from 'recharts';

interface ParceiroMetricsData {
  carregadores: {
    leads: number;
    propostas: number;
    contratos: number;
    valorContratos: number;
    comissoes: number;
  };
  fotovoltaico: {
    leads: number;
    propostas: number;
    contratos: number;
    valorContratos: number;
    comissoes: number;
  };
  rede?: {
    parceiros: number;
    carregadores: {
      leads: number;
      propostas: number;
      contratos: number;
    };
    fotovoltaico: {
      leads: number;
      propostas: number;
      contratos: number;
    };
    comissoesPai: number;
  };
}

export interface EvolucaoMensalItem {
  mes: string;
  mesLabel: string;
  leads: number;
  contratos: number;
  valor: number;
}

export interface ComparativoMesItem {
  leads: number;
  contratos: number;
  valor: number;
}

export interface ResumoComissoes {
  gerada: number;
  paga: number;
}

const formatMoeda = (valor: number) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
};

export function ParceiroDashboard() {
  const permissions = usePermissions();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<ParceiroMetricsData | null>(null);
  const [evoluacaoMensal, setEvoluacaoMensal] = useState<EvolucaoMensalItem[]>([]);
  const [comparativoMes, setComparativoMes] = useState<{
    mesAtual: ComparativoMesItem;
    mesAnterior: ComparativoMesItem;
  } | null>(null);
  const [resumoComissoes, setResumoComissoes] = useState<ResumoComissoes | null>(null);

  useEffect(() => {
    async function loadData() {
      if (!permissions?.userProfile?.id || permissions.userRole !== 'parceiro') return;

      try {
        setLoading(true);
        const userId = permissions.userProfile.id;
        const isParceiroPai = permissions.userProfile.pode_criar_rede_parceiros === true;

        // 1. Carregadores / wallbox: leads com produto legado ou "Instalação de Wallbox"
        const { data: leadsCarregadoresRows } = await supabase
          .from('leads')
          .select('id, created_at')
          .eq('parceiro_id', userId)
          .in('produto', [...PRODUTOS_LEAD_CARREGADOR]);
        const leadsCarregadoresIds = (leadsCarregadoresRows || []).map((r: { id: string }) => r.id);
        const leadsCarregadoresComData = (leadsCarregadoresRows || []).map((r: { id: string; created_at?: string | null }) => ({ id: r.id, created_at: r.created_at }));
        // Leads Captados = total de leads com produto Carregadores (inclui quem virou cliente)
        const leadsCarregadoresCount = leadsCarregadoresIds.length;

        // 2. Propostas e contratos de carregadores (usar os lead_ids obtidos acima)
        let propostasCarregadoresCount = 0;
        let contratosCarregadoresCount = 0;
        let valorContratosCarregadores = 0;
        let contratosCarregadoresData: { id: string; lead_id: string; proposta_id: string; created_at?: string | null }[] = [];
        let propostasCarr: { id: string; valor_total: number }[] = [];
        if (leadsCarregadoresIds.length > 0) {
          const { data: propostasCarregadoresLeads } = await supabase
            .from('propostas_carregadores')
            .select('id, lead_id')
            .in('lead_id', leadsCarregadoresIds);
          propostasCarregadoresCount = new Set((propostasCarregadoresLeads || []).map((p: any) => p.lead_id)).size;

          const { data: contratosCarregadoresDataRows } = await supabase
            .from('contratos_carregadores')
            .select('id, lead_id, proposta_id, created_at')
            .in('lead_id', leadsCarregadoresIds)
            .not('contrato_pdf_url', 'is', null);
          contratosCarregadoresData = (contratosCarregadoresDataRows || []).map((c: any) => ({
            id: c.id,
            lead_id: c.lead_id,
            proposta_id: c.proposta_id,
            created_at: c.created_at,
          }));
          contratosCarregadoresCount = contratosCarregadoresData.length;

          if (contratosCarregadoresData.length > 0) {
            const { data: propostasCarrData } = await supabase
              .from('propostas_carregadores')
              .select('id, valor_total')
              .in('id', contratosCarregadoresData.map((c) => c.proposta_id));
            propostasCarr = (propostasCarrData || []).map((p: any) => ({ id: p.id, valor_total: Number(p.valor_total) || 0 }));
            valorContratosCarregadores = propostasCarr.reduce((acc: number, p) => acc + p.valor_total, 0);
          }
        }

        // 3. Fotovoltaico: produto fora de carregador/wallbox ou nulo
        const { data: leadsFotovoltaicoRows } = await supabase
          .from('leads')
          .select('id, created_at')
          .eq('parceiro_id', userId)
          .or(supabaseOrProdutoNaoCarregador());
        const leadsFotovoltaicoIds = (leadsFotovoltaicoRows || []).map((r: { id: string }) => r.id);
        const leadsFotovoltaicoComData = (leadsFotovoltaicoRows || []).map((r: { id: string; created_at?: string | null }) => ({ id: r.id, created_at: r.created_at }));
        const leadsFotovoltaicoCount = leadsFotovoltaicoIds.length;

        // 4. Contratos fotovoltaico (com proposta_id para comissão gerada e created_at para evolução)
        let contratosSun7: { id: string; valor_total: number; lead_id: string; status: string; proposta_id: string | null; created_at: string | null }[] = [];
        let valorContratosFotovoltaico = 0;
        if (leadsFotovoltaicoIds.length > 0) {
          const { data: contratosSun7Data } = await supabase
            .from('contratos_sun7')
            .select('id, valor_total, lead_id, status, proposta_id, created_at')
            .in('status', ['proposta_aceita', 'contrato_gerado', 'assinado', 'em_andamento'])
            .in('lead_id', leadsFotovoltaicoIds);
          contratosSun7 = (contratosSun7Data || []).map((c: any) => ({
            id: c.id,
            valor_total: Number(c.valor_total) || 0,
            lead_id: c.lead_id,
            status: c.status,
            proposta_id: c.proposta_id,
            created_at: c.created_at,
          }));
          valorContratosFotovoltaico = contratosSun7.reduce((acc, c) => acc + c.valor_total, 0);
        }

        // 5. Propostas fotovoltaico
        let propostasFotovoltaicoCount = 0;
        if (leadsFotovoltaicoIds.length > 0) {
          const { data: propostasClientes } = await supabase
            .from('propostas_clientes')
            .select('id, lead_id')
            .in('lead_id', leadsFotovoltaicoIds);
          propostasFotovoltaicoCount = new Set((propostasClientes || []).map((p: any) => p.lead_id)).size;
        }

        // 7. Comissões geradas: pelo fechamento do contrato (proposta), não pelo lançamento na página financeiro
        let comissaoGeradaFoto = 0;
        if (contratosSun7.length > 0) {
          const propostaIds = contratosSun7.map((c: any) => c.proposta_id).filter(Boolean);
          if (propostaIds.length > 0) {
            const { data: propostasComissao } = await supabase
              .from('propostas_clientes')
              .select('id, comissao_parceiro_valor')
              .in('id', propostaIds);
            comissaoGeradaFoto = (propostasComissao || []).reduce((acc: number, p: any) => acc + (Number(p.comissao_parceiro_valor) || 0), 0);
          }
        }

        const comissoesCarr = await getResumoComissaoCarregadores(userId);
        const comissaoGeradaCarr = comissoesCarr?.length ? (comissoesCarr[0].total_comissoes ?? 0) : 0;

        const newData: ParceiroMetricsData = {
          carregadores: {
            leads: leadsCarregadoresCount,
            propostas: propostasCarregadoresCount,
            contratos: contratosCarregadoresCount,
            valorContratos: valorContratosCarregadores,
            comissoes: comissaoGeradaCarr,
          },
          fotovoltaico: {
            leads: leadsFotovoltaicoCount,
            propostas: propostasFotovoltaicoCount,
            contratos: contratosSun7.length,
            valorContratos: valorContratosFotovoltaico,
            comissoes: comissaoGeradaFoto,
          }
        };

        // 7. Rede de Parceiros (if applicable) — separado por produto: carregadores e fotovoltaico
        if (isParceiroPai) {
          const { data: parceirosRede } = await supabase
            .from('profiles')
            .select('id')
            .eq('parceiro_cadastrado_por', userId);
          
          if (parceirosRede && parceirosRede.length > 0) {
            const redeIds = parceirosRede.map(p => p.id);
            
            // Leads da rede — carregador / wallbox
            const { data: leadsRedeCarregadores } = await supabase
              .from('leads')
              .select('id')
              .in('parceiro_id', redeIds)
              .in('produto', [...PRODUTOS_LEAD_CARREGADOR]);
            const leadsRedeCarregadoresIds = (leadsRedeCarregadores || []).map((l: { id: string }) => l.id);

            // Leads da rede — Fotovoltaico (demais produtos ou null)
            const { data: leadsRedeFotovoltaico } = await supabase
              .from('leads')
              .select('id')
              .in('parceiro_id', redeIds)
              .or(supabaseOrProdutoNaoCarregador());
            const leadsRedeFotovoltaicoIds = (leadsRedeFotovoltaico || []).map((l: { id: string }) => l.id);

            // Propostas e contratos — Carregadores (rede)
            let propostasRedeCarregadores = 0;
            let contratosRedeCarregadores = 0;
            if (leadsRedeCarregadoresIds.length > 0) {
              const { data: pRedeCarr } = await supabase.from('propostas_carregadores').select('lead_id').in('lead_id', leadsRedeCarregadoresIds);
              propostasRedeCarregadores = new Set((pRedeCarr || []).map((p: any) => p.lead_id)).size;
              const { data: cRedeCarr } = await supabase.from('contratos_carregadores').select('id').in('lead_id', leadsRedeCarregadoresIds).not('contrato_pdf_url', 'is', null);
              contratosRedeCarregadores = (cRedeCarr || []).length;
            }

            // Propostas e contratos — Fotovoltaico (rede)
            let propostasRedeFotovoltaico = 0;
            let contratosRedeFotovoltaico = 0;
            if (leadsRedeFotovoltaicoIds.length > 0) {
              const { data: pRedeFoto } = await supabase.from('propostas_clientes').select('lead_id').in('lead_id', leadsRedeFotovoltaicoIds);
              propostasRedeFotovoltaico = new Set((pRedeFoto || []).map((p: any) => p.lead_id)).size;
              const { data: cRedeFoto } = await supabase.from('contratos_sun7').select('id').in('lead_id', leadsRedeFotovoltaicoIds).in('status', ['proposta_aceita', 'contrato_gerado', 'assinado', 'em_andamento']);
              contratosRedeFotovoltaico = (cRedeFoto || []).length;
            }

            // Comissões geradas para o parceiro pai (carregadores da rede)
            const { data: resumosPai } = await supabase.rpc('get_carregadores_comissao_resumo_parceiro_pai', {
              p_parceiro_pai_id: userId,
            });
            const comissaoGeradaPai = (resumosPai as any[] || []).reduce((acc, r) => acc + (Number(r.total_comissoes) || 0), 0);

            newData.rede = {
              parceiros: parceirosRede.length,
              carregadores: {
                leads: leadsRedeCarregadoresIds.length,
                propostas: propostasRedeCarregadores,
                contratos: contratosRedeCarregadores,
              },
              fotovoltaico: {
                leads: leadsRedeFotovoltaicoIds.length,
                propostas: propostasRedeFotovoltaico,
                contratos: contratosRedeFotovoltaico,
              },
              comissoesPai: comissaoGeradaPai
            };
          }
        }

        // Evolução mensal (últimos 12 meses) e comparativo mês atual vs anterior
        const agora = new Date();
        const mesesEvolucao: EvolucaoMensalItem[] = [];
        const propostasCarrMap = new Map(propostasCarr.map((p) => [p.id, p.valor_total]));

        for (let i = 11; i >= 0; i--) {
          const ref = subMonths(agora, i);
          const inicio = startOfMonth(ref).getTime();
          const fim = endOfMonth(ref).getTime();
          const mesKey = format(ref, 'yyyy-MM');
          const mesLabel = format(ref, 'MMM/yy', { locale: ptBR });

          const inMonth = (createdAt: string | null | undefined) => {
            if (!createdAt) return false;
            const t = new Date(createdAt).getTime();
            return t >= inicio && t <= fim;
          };

          let leadsCount = 0;
          leadsCarregadoresComData.forEach((l) => { if (inMonth(l.created_at)) leadsCount++; });
          leadsFotovoltaicoComData.forEach((l) => { if (inMonth(l.created_at)) leadsCount++; });

          let contratosCount = 0;
          let valorCount = 0;
          contratosCarregadoresData.forEach((c) => {
            if (inMonth(c.created_at)) {
              contratosCount++;
              valorCount += propostasCarrMap.get(c.proposta_id) ?? 0;
            }
          });
          contratosSun7.forEach((c) => {
            if (inMonth(c.created_at)) {
              contratosCount++;
              valorCount += c.valor_total;
            }
          });

          mesesEvolucao.push({
            mes: mesKey,
            mesLabel,
            leads: leadsCount,
            contratos: contratosCount,
            valor: valorCount,
          });
        }
        setEvoluacaoMensal(mesesEvolucao);

        const mesAtualInicio = startOfMonth(agora).getTime();
        const mesAtualFim = endOfMonth(agora).getTime();
        const mesAnteriorRef = subMonths(agora, 1);
        const mesAnteriorInicio = startOfMonth(mesAnteriorRef).getTime();
        const mesAnteriorFim = endOfMonth(mesAnteriorRef).getTime();

        const inMesAtual = (createdAt: string | null | undefined) => {
          if (!createdAt) return false;
          const t = new Date(createdAt).getTime();
          return t >= mesAtualInicio && t <= mesAtualFim;
        };
        const inMesAnterior = (createdAt: string | null | undefined) => {
          if (!createdAt) return false;
          const t = new Date(createdAt).getTime();
          return t >= mesAnteriorInicio && t <= mesAnteriorFim;
        };

        const agg = (pred: (createdAt: string | null | undefined) => boolean) => {
          let l = 0, c = 0, v = 0;
          leadsCarregadoresComData.forEach((x) => { if (pred(x.created_at)) l++; });
          leadsFotovoltaicoComData.forEach((x) => { if (pred(x.created_at)) l++; });
          contratosCarregadoresData.forEach((x) => {
            if (pred(x.created_at)) {
              c++;
              v += propostasCarrMap.get(x.proposta_id) ?? 0;
            }
          });
          contratosSun7.forEach((x) => {
            if (pred(x.created_at)) {
              c++;
              v += x.valor_total;
            }
          });
          return { leads: l, contratos: c, valor: v };
        };
        setComparativoMes({
          mesAtual: agg(inMesAtual),
          mesAnterior: agg(inMesAnterior),
        });

        setMetrics(newData);

        // Resumo comissões (geradas vs pagas) via API financeiro
        try {
          const resFin = await fetchWithAuth(`/api/parceiros/${userId}/financeiro`);
          if (resFin.ok) {
            const dataFin = await resFin.json();
            const contratosFoto = dataFin.contratos || [];
            const contratosCarr = dataFin.contratosCarregadores || [];
            const comCarr = dataFin.comissaoCarregadores;
            const comCarrPai = dataFin.comissaoCarregadoresParceiroPai;
            const gerada =
              contratosFoto.reduce((s: number, x: any) => s + (Number(x.comissao_parceiro_valor) || 0), 0) +
              (Number(comCarr?.total_comissoes) || 0) +
              (Number(comCarrPai?.total_comissoes) || 0);
            const paga =
              contratosFoto.reduce((s: number, x: any) => s + (x.pagamentos || []).reduce((sp: number, p: any) => sp + (Number(p.valor_pago) || 0), 0), 0) +
              contratosCarr.reduce((s: number, x: any) => s + (x.pagamentos || []).reduce((sp: number, p: any) => sp + (Number(p.valor_pago) || 0), 0), 0) +
              (Number(comCarr?.total_pago) || 0) +
              (Number(comCarrPai?.total_pago) || 0);
            setResumoComissoes({ gerada, paga });
          }
        } catch {
          setResumoComissoes(null);
        }
      } catch (error) {
        console.error("Erro ao carregar dados do dashboard de parceiro:", error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [permissions?.userProfile?.id]);

  if (loading || !metrics) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-8 h-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const MetricCard = ({ title, value, icon: Icon, color, suffix = "" }: any) => {
    const colorClasses: Record<string, string> = {
      blue: "bg-blue-500/10 text-blue-600",
      green: "bg-emerald-500/10 text-emerald-600",
      purple: "bg-purple-500/10 text-purple-600",
      orange: "bg-orange-500/10 text-orange-600",
      rose: "bg-rose-500/10 text-rose-600",
      amber: "bg-amber-500/10 text-amber-600"
    };

    return (
      <Card className="shadow-sm overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`shrink-0 p-2.5 rounded-xl ${colorClasses[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-muted-foreground truncate">{title}</p>
              <p className="text-base font-bold mt-0.5 text-foreground break-words leading-tight">
                {value}{suffix}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderSection = (title: string, icon: any, data: any, colorPrefix: string) => {
    const taxaConversao = data.leads > 0 ? ((data.contratos / data.leads) * 100).toFixed(1) : 0;

    return (
      <div className="space-y-4 mb-8">
        <div className="flex items-center gap-2 pb-2 border-b border-border/50">
          {React.createElement(icon, { className: `w-6 h-6 text-${colorPrefix}-500` })}
          <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <MetricCard title="Leads Captados" value={data.leads} icon={Users} color={colorPrefix} />
          <MetricCard title="Propostas Geradas" value={data.propostas} icon={FileText} color="blue" />
          <MetricCard title="Contratos Fechados" value={data.contratos} icon={Target} color="green" />
          <MetricCard title="Valor Fechado" value={formatMoeda(data.valorContratos || 0)} icon={DollarSign} color="purple" />
          <MetricCard title="Comissões Geradas" value={formatMoeda(data.comissoes || 0)} icon={Handshake} color="amber" />
          <MetricCard title="Conversão" value={taxaConversao} suffix="%" icon={Percent} color="rose" />
        </div>
      </div>
    );
  };

  const mesAtualLabel = format(new Date(), 'MMMM yyyy', { locale: ptBR });
  const mesAnteriorLabel = format(subMonths(new Date(), 1), 'MMMM yyyy', { locale: ptBR });

  const comparativoCards = comparativoMes && (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Comparativo: mês atual vs anterior
          </CardTitle>
          <CardDescription className="text-xs">
            {mesAtualLabel} vs {mesAnteriorLabel}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <span className="text-sm">Leads</span>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{comparativoMes.mesAtual.leads}</span>
              <span className="text-muted-foreground text-xs">(atual)</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-muted-foreground text-sm">{comparativoMes.mesAnterior.leads}</span>
              <span className="text-muted-foreground text-xs">(anterior)</span>
              {comparativoMes.mesAnterior.leads > 0 && (
                <span className={`text-xs flex items-center gap-0.5 ${comparativoMes.mesAtual.leads >= comparativoMes.mesAnterior.leads ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {comparativoMes.mesAtual.leads >= comparativoMes.mesAnterior.leads ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {(((comparativoMes.mesAtual.leads - comparativoMes.mesAnterior.leads) / comparativoMes.mesAnterior.leads) * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <span className="text-sm">Contratos</span>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{comparativoMes.mesAtual.contratos}</span>
              <span className="text-muted-foreground text-xs">(atual)</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-muted-foreground text-sm">{comparativoMes.mesAnterior.contratos}</span>
              <span className="text-muted-foreground text-xs">(anterior)</span>
              {comparativoMes.mesAnterior.contratos > 0 && (
                <span className={`text-xs flex items-center gap-0.5 ${comparativoMes.mesAtual.contratos >= comparativoMes.mesAnterior.contratos ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {comparativoMes.mesAtual.contratos >= comparativoMes.mesAnterior.contratos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {(((comparativoMes.mesAtual.contratos - comparativoMes.mesAnterior.contratos) / comparativoMes.mesAnterior.contratos) * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <span className="text-sm">Valor fechado</span>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <span className="font-semibold">{formatMoeda(comparativoMes.mesAtual.valor)}</span>
              <span className="text-muted-foreground text-xs">(atual)</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-muted-foreground text-sm">{formatMoeda(comparativoMes.mesAnterior.valor)}</span>
              <span className="text-muted-foreground text-xs">(anterior)</span>
              {comparativoMes.mesAnterior.valor > 0 && (
                <span className={`text-xs flex items-center gap-0.5 ${comparativoMes.mesAtual.valor >= comparativoMes.mesAnterior.valor ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {comparativoMes.mesAtual.valor >= comparativoMes.mesAnterior.valor ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {(((comparativoMes.mesAtual.valor - comparativoMes.mesAnterior.valor) / comparativoMes.mesAnterior.valor) * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const dadosPizza = [
    { name: 'Carregadores Veiculares', value: metrics.carregadores.valorContratos || 0, color: '#f97316' },
    { name: 'Energia Fotovoltaica', value: metrics.fotovoltaico.valorContratos || 0, color: '#f59e0b' },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-6">
      {/* Seções principais no topo */}
      {renderSection(
        "Carregadores Veiculares", 
        BatteryCharging, 
        metrics.carregadores, 
        "orange"
      )}

      {renderSection(
        "Energia Fotovoltaica", 
        Zap, 
        metrics.fotovoltaico, 
        "amber"
      )}

      {/* Comissões geradas vs pagas (3ª seção) */}
      {resumoComissoes != null && (
        <Card className="shadow-sm border-amber-200/50 dark:border-amber-900/30">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Handshake className="w-5 h-5 text-amber-600" />
              Comissões: geradas vs pagas
            </CardTitle>
            <CardDescription>Total de comissões geradas pelos seus contratos e valor já recebido</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg bg-amber-500/10 p-4 border border-amber-200/50 dark:border-amber-800/50">
                <p className="text-sm font-medium text-muted-foreground">Comissões geradas</p>
                <p className="text-xl font-bold text-amber-700 dark:text-amber-400">{formatMoeda(resumoComissoes.gerada)}</p>
              </div>
              <div className="rounded-lg bg-emerald-500/10 p-4 border border-emerald-200/50 dark:border-emerald-800/50">
                <p className="text-sm font-medium text-muted-foreground">Comissões pagas</p>
                <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{formatMoeda(resumoComissoes.paga)}</p>
              </div>
            </div>
            {resumoComissoes.gerada > 0 && (
              <p className="text-sm text-muted-foreground mt-3">
                Saldo a receber: <span className="font-semibold text-foreground">{formatMoeda(resumoComissoes.gerada - resumoComissoes.paga)}</span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Gráficos: evolução e valor por produto */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolução no tempo */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Evolução no tempo</CardTitle>
            <CardDescription>Leads, contratos e valor fechado por mês (últimos 12 meses)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={evoluacaoMensal}>
                  <defs>
                    <linearGradient id="colorValor" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mesLabel" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tickFormatter={(v) => `R$ ${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} tick={{ fontSize: 11 }} />
                  <RechartsTooltip
                    formatter={(value: number, name: string) => {
                      const key = (name || '').toLowerCase();
                      const displayValue = key === 'valor' ? formatMoeda(value) : value;
                      const label = key === 'leads' ? 'Leads captados' : key === 'contratos' ? 'Contratos fechados' : 'Valor fechado (R$)';
                      return [displayValue, label];
                    }}
                    labelFormatter={(label) => label}
                  />
                  <Area yAxisId="left" type="monotone" dataKey="leads" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.4} name="Leads" />
                  <Area yAxisId="left" type="monotone" dataKey="contratos" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.4} name="Contratos" />
                  <Area yAxisId="right" type="monotone" dataKey="valor" stroke="#8b5cf6" fill="url(#colorValor)" name="Valor" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Carregadores vs Fotovoltaico (valor) */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Valor fechado por produto</CardTitle>
            <CardDescription>Carregadores Veiculares vs Energia Fotovoltaica</CardDescription>
          </CardHeader>
          <CardContent>
            {dadosPizza.length > 0 ? (
              <div className="h-[280px] w-full flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dadosPizza} layout="vertical" margin={{ top: 8, right: 24, left: 80, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => `R$ ${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                    <YAxis type="category" dataKey="name" width={75} tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(value: number) => [formatMoeda(value), 'Valor']} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
                      {dadosPizza.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
                Nenhum valor fechado ainda para exibir
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Comparativo mês atual vs anterior */}
      {comparativoCards}

      {metrics.rede && (
        <div className="space-y-6 mb-8">
          <div className="flex items-center gap-2 pb-2 border-b border-border/50">
            <Users className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-foreground">Sua Rede de Parceiros</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MetricCard title="Tamanho da Rede" value={metrics.rede.parceiros} icon={Handshake} color="blue" />
            <MetricCard title="Suas Comissões (Pai)" value={formatMoeda(metrics.rede.comissoesPai || 0)} icon={DollarSign} color="amber" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Carregadores Veiculares — Rede */}
            <Card className="shadow-sm overflow-hidden border-orange-200/50 dark:border-orange-900/30">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-orange-500/10 text-orange-600">
                    <BatteryCharging className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-base">Carregadores Veiculares (Rede)</CardTitle>
                </div>
                <CardDescription>Leads, propostas e contratos captados pela sua rede neste produto</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <MetricCard title="Leads" value={metrics.rede.carregadores.leads} icon={Users} color="orange" />
                  <MetricCard title="Propostas" value={metrics.rede.carregadores.propostas} icon={FileText} color="blue" />
                  <MetricCard title="Contratos" value={metrics.rede.carregadores.contratos} icon={Target} color="green" />
                </div>
              </CardContent>
            </Card>

            {/* Energia Fotovoltaica — Rede */}
            <Card className="shadow-sm overflow-hidden border-amber-200/50 dark:border-amber-900/30">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-amber-500/10 text-amber-600">
                    <Zap className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-base">Energia Fotovoltaica (Rede)</CardTitle>
                </div>
                <CardDescription>Leads, propostas e contratos captados pela sua rede neste produto</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <MetricCard title="Leads" value={metrics.rede.fotovoltaico.leads} icon={Users} color="amber" />
                  <MetricCard title="Propostas" value={metrics.rede.fotovoltaico.propostas} icon={FileText} color="blue" />
                  <MetricCard title="Contratos" value={metrics.rede.fotovoltaico.contratos} icon={Target} color="green" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
